import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

import styles from "./index.module.css";

import GifAnimation from "../GifAnimation";
import { isIphone } from "../../atoms";

import {
  PREVIEW_VIDEO_RESOLUTION,
  EMPTY_GIF,
  VIDEO_RATIO_MOBILE,
  VIDEO_RATIO_PC
} from "../../../constants";
import DUMMY_VIDEO from "../../../assets/videos/dummy_sml.mp4";

const Player = ({
  imgSrc,
  vidSrc,
  alt,
  className,
  height,
  width,
  playing,
  previewImg,
  ...props
}) => {
  // loading sequence
  const [ready, setReady] = useState(false);
  const handleReady = e => {
    setReady(true);
  };

  // width responsive
  const [wWidth, setWWidth] = useState(window.innerWidth);
  useEffect(() => {
    window.addEventListener("resize", handleCheckWidth, { passive: true });
    return () =>
      window.removeEventListener("resize", handleCheckWidth, { passive: true });
  }, []);

  const handleCheckWidth = () => {
    requestAnimationFrame(() => setWWidth(window.innerWidth));
  };

  // 0.6 is the window size at pc width (WORK, TEAM, ABOUT each 33%)
  const isMobile = wWidth < 750;
  const vidWidth = isMobile ? wWidth : (wWidth * 0.665) / 2 - 10;
  const vidHeight = isMobile
    ? wWidth / VIDEO_RATIO_MOBILE + 15
    : (wWidth * 0.6) / VIDEO_RATIO_PC / 2 - 10;

  // onHover logic (ONLY PC)
  const playerRef = useRef();
  useEffect(() => {
    if (playerRef && playerRef.current && !isMobile) {
      const el = playerRef && playerRef.current;
      el.addEventListener("mouseenter", handlePlay, { passive: true });
      el.addEventListener("mouseleave", handlePause, { passive: true });
    }

    return () => {
      const el = playerRef && playerRef.current;
      el.removeEventListener("mouseenter", handlePlay, { passive: true });
      el.removeEventListener("mouseleave", handlePause, { passive: true });
    };
  }, [playerRef, isMobile]);

  // playing logic (mobile)
  useEffect(() => {
    if (ready && isMobile) {
      if (playing && playerRef.current.paused) {
        handlePlay();
      } else if (!playing && !playerRef.current.paused) {
        handlePause();
      }
    }
  }, [ready, playing, playerRef, isMobile]);

  const [isPlaying, setIsPlaying] = useState(false);
  const handlePlay = async () => {
    try {
      playerRef.current.play();
      setIsPlaying(true);
    } catch (e) {
      console.error("PLAYER ERORR: ", e);
    }
  };

  const handlePause = async () => {
    try {
      await playerRef.current.pause();
      setIsPlaying(false);
    } catch (e) {
      console.error("PLAYER ERORR: ", e);
    }
  };

  return (
    <div
      className={`${styles.videoContainer} ${className}`}
      style={{
        height: height || vidHeight,
        width: width || vidWidth
      }}
      {...props}
    >
      <GifAnimation
        gifSrc={EMPTY_GIF}
        toggle={ready}
        className={styles.loading}
        noHover
      >
        {imgSrc && (
          <motion.img
            src={imgSrc}
            alt={imgSrc || "no image"}
            className={styles.img}
            initial={{ opacity: 1 }}
            animate={{ opacity: isPlaying ? 0 : 1 }}
          />
        )}

        <video
          ref={playerRef}
          className={styles.video}
          onLoadedMetadata={handleReady}
          loop
          playsInline
          muted
        >
          {/* https://stackoverflow.com/questions/7323053/dynamically-using-the-first-frame-as-poster-in-html5-video */}
          <source src={`${vidSrc}`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </GifAnimation>
    </div>
  );
};

export default Player;
